import {getKarelUnitTestReference} from '../../models/unitTests/karel';
import {
  UnitTestLoaderParameters,
  UnitTestSchema,
  WithRunnerData,
} from '../UnitTestSchema';
import {
  EMPTY_TEST_RESULT,
  getFirstTestFailed,
  UnitTestResults,
} from '../unitTestResults';
import {cloneDeep as deepCopy} from 'lodash-es';
import {areWorldsEqual} from '@cip/code-runner';
import {getDoc} from 'firebase/firestore';
import {KarelEditor} from './KarelEditor';
import {type LoadedKarelUnitTestData} from './types';
import {getAllFileNames} from 'ide/utils/general';
import {compileKarelCode} from 'ide/utils/codeExecution';
import type {TranslationFunction} from 'components/translation/utils';
import {getAssignmentMetadataReference} from 'assignments/models/assignmentMetadata';

async function loader({
  courseId,
  assignmentId,
}: UnitTestLoaderParameters): Promise<LoadedKarelUnitTestData | null> {
  const karelUnitTestReference = getKarelUnitTestReference(
    courseId,
    assignmentId,
  );
  const assignmentReference = getAssignmentMetadataReference(
    courseId,
    assignmentId,
  );
  const karelUnitTestDoc = await getDoc(karelUnitTestReference);
  if (!karelUnitTestDoc.exists) {
    return null;
  }
  const assignmentDoc = await getDoc(assignmentReference);
  if (!assignmentDoc.exists) {
    return null;
  }
  const assignmentData = assignmentDoc.data();
  const unitTests = karelUnitTestDoc.data();
  const skipKarelCompiler = assignmentData.skipKarelCompiler ?? false;
  return {
    unitTests,
    skipKarelCompiler,
  };
}

async function runner({
  loadedData,
  pyodideClient,
  filesCode,
  fileStructure,
}: WithRunnerData<LoadedKarelUnitTestData>): Promise<UnitTestResults> {
  const {unitTests} = loadedData ?? {unitTests: []};
  // make sure that the unitTests are valid
  if (!unitTests || unitTests.length === 0) {
    return EMPTY_TEST_RESULT;
  }

  const testResults = [];
  const allFileNames = getAllFileNames(fileStructure);
  const mainFile = allFileNames.find(entry => entry.name === 'main.py');
  if (!mainFile) {
    return {
      testsRun: true,
      testResults: [
        {
          isSuccess: false,
          name: 'Karel Pre/Post World Comparison',
          usedAi: false,
          rawResult: 'No main.py file found',
        },
      ],
    };
  }

  // make sure code compiles with karel compiler
  const studentCode = filesCode[mainFile.id].content;
  const karelWorldState = unitTests[0].pre; // use the first test as the world state
  if (!loadedData.skipKarelCompiler) {
    const compileResult = compileKarelCode(studentCode, karelWorldState);
    if (compileResult.error) {
      return {
        testsRun: true,
        testResults: [
          {
            isSuccess: false,
            name: 'Karel Code Compilation',
            usedAi: false,
            rawResult: compileResult.error,
          },
        ],
      };
    }
  }

  // Unit tests run with no inter-step sleep — instant playback for
  // grading. Save the IDE's slider value, override to 0 for the test
  // loop, restore on exit so the next IDE run respects the slider
  // again. The plug's pre-run hook (registered on first
  // setKarelSleepTime call) ensures the override re-asserts before
  // every test's runCode in the loop.
  const previousSleepSeconds = pyodideClient.getKarelSleepTime();
  pyodideClient.setKarelSleepTime(0);
  try {
    for (const unitTest of unitTests) {
      const startState = deepCopy(unitTest.pre);
      let rawResult;
      const results = await pyodideClient.runCode(
        filesCode[mainFile.id].content,
        {
          apiOverrides: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
            "karel": (func: string, args: any) => {
              if (func === 'setKarelWorldState') {
                rawResult = args
              }
            }
          },
          ioOverrides: {
            onRunEnd: () => {
              console.log('Karel run ended');
            },
            // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
            error: (message: string) => {}
          },
          cacheState: {
            "karelStartState": startState
          },
        }
      );

      const observedPost = rawResult;
      const expectedPost = unitTest.post;
      const hasError = results.error && results.error.length > 0;
      const worldsEqual = areWorldsEqual(expectedPost, observedPost);
      const isSuccess = worldsEqual && !hasError;
      testResults.push({
        isSuccess,
        name: unitTest.name,
        usedAi: false,
        rawResult: rawResult,
      });
    }
  } finally {
    pyodideClient.setKarelSleepTime(previousSleepSeconds);
  }
  return {
    testsRun: true,
    testResults,
  };
}

function getFailureMessage(results: UnitTestResults, t: TranslationFunction) {
  const firstTestFailed = getFirstTestFailed(results);
  const numTests = results.testResults.length;
  let text = t(
    "The program finished running, but didn't solve the problem.",
  ) as string;
  if (numTests === 1) {
    const firstTest = results.testResults[0];
    if (firstTest.name === 'Karel Code Compilation') {
      text = t(
        "The program didn't compile. You may be using syntax that is not supported for Karel, like advanced Python features. Or, you have a typo :)",
      ) as string;
      console.log(firstTest.rawResult);
    } else {
      text = t(
        "Karel finished running but didn't solve the problem. Either Karel is in the wrong place or the beepers are.",
      ) as string;
    }
  } else {
    text =
      (t(
        "Karel finished running but didn't solve the problem. Check the world named ",
      ) as string) + firstTestFailed;
  }
  return text;
}

export const KAREL_UNIT_TEST_SCHEMA: UnitTestSchema<LoadedKarelUnitTestData> = {
  loader,
  runner,
  EditorComponent: KarelEditor,
  unitTestType: 'karel',
  displayName: 'Karel Pre/Post World Comparison',
  tooltipDescription:
    'Compare the world state before and after running the student code to the solution.',
  getFailureMessage,
};
